import {ChartType} from 'common/report/constants';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {serializedRelativeDatesToRollingFullLengthDateRange} from 'common/util/date-converters';
import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getDefaultTrendUnit} from 'common/util/date';
import {getMergedBookmarkEventAndCohortFilters} from 'common/report/util/bookmark-overrides';

import {API_CHART_TYPE_TO_COMMON_CHART_TYPE, COMMON_CHART_TYPE_TO_API_CHART_TYPE} from './constants';
import {
  isRelativeRollingDateRange,
  isRetentionCurveBasedChart,
  RetentionBookmarkFilterByEvent,
  RetentionBookmarkParams,
} from './types';

export function shouldSyncDateAndRetentionUnits(isAddiction, chartType) {
  // In retention curve, the retention unit and date unit have to the be same. In `addiction` mode, only the
  // `dateUnit` (and not `retentionUnit`) is used, so syncing isn't necessary.
  return !isAddiction && isRetentionCurveBasedChart(chartType);
}

export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: RetentionBookmarkParams,
  additionalFilters: PropertyFilter[],
): RetentionBookmarkParams {
  const mergedFilters = getMergedBookmarkEventAndCohortFilters(
    {
      filter_by_event: bookmarkParams.filter_by_event as RetentionBookmarkFilterByEvent,
      filter_by_cohort: bookmarkParams.filter_by_cohort,
    },
    additionalFilters,
  );

  return {
    ...bookmarkParams,
    // we can cast to RetentionBookmarkFilterByEvent because filters with ResourceType.Other will not be present
    filter_by_event: (mergedFilters.filter_by_event ?? {}) as RetentionBookmarkFilterByEvent,
    filter_by_cohort: mergedFilters.filter_by_cohort ?? {},
  };
}

export function getDefaultRetentionTrendUnit(dateRange: DateRange, isAddiction: boolean): TimeUnit {
  const numDays = dateRange.getIntervalDays();
  let unit = getDefaultTrendUnit(numDays);

  const addictionMoreThan14Days = isAddiction && unit === TimeUnit.Day && numDays >= 14;
  unit = addictionMoreThan14Days ? TimeUnit.Week : unit;
  return unit === TimeUnit.Hour ? TimeUnit.Day : (unit as TimeUnit);
}

function shouldOverrideDateUnit(bookmarkParams: RetentionBookmarkParams, dateRangeOverride: DateRange): boolean {
  let bookmarkDateRange: BookmarkDateRange;
  if (isRelativeRollingDateRange(bookmarkParams.date_range)) {
    bookmarkDateRange = serializedRelativeDatesToRollingFullLengthDateRange({
      relativeFrom: Number(bookmarkParams.date_range.from),
      relativeTo: Number(bookmarkParams.date_range.to),
    });
  } else {
    bookmarkDateRange = bookmarkParams.date_range;
  }

  return !DateRange.fromBookmarkFormat(bookmarkDateRange).equals(dateRangeOverride);
}

export function getBookmarkParamsWithOverrides(
  bookmarkParams: RetentionBookmarkParams,
  chartTypeOverride: ChartType,
  dateRangeOverride: DateRange,
  overrideFilters: PropertyFilter[],
) {
  const isAddiction = bookmarkParams.retention_type === `addiction`;

  bookmarkParams = getBookmarkParamsWithAdditionalFilters(bookmarkParams, overrideFilters);

  const dateRange = dateRangeOverride ? dateRangeOverride.getBookmarkFormat() : bookmarkParams.date_range;
  const dateUnit =
    dateRangeOverride && shouldOverrideDateUnit(bookmarkParams, dateRangeOverride)
      ? getDefaultRetentionTrendUnit(dateRangeOverride, isAddiction)
      : bookmarkParams.date_unit;

  const chartType = chartTypeOverride
    ? chartTypeOverride
    : API_CHART_TYPE_TO_COMMON_CHART_TYPE[bookmarkParams.chart_type];

  return {
    ...bookmarkParams,
    chart_type: COMMON_CHART_TYPE_TO_API_CHART_TYPE[chartType],
    date_range: dateRange,
    date_unit: dateUnit,
    ...(shouldSyncDateAndRetentionUnits(isAddiction, chartType) ? {retention_unit: dateUnit} : {}),
  };
}
